import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import TitleCard from "@/components/feature/TitleCard";
import { titles } from "@/mocks/titles";
import { usePageMeta } from "@/hooks/usePageMeta";

const HOT_KEYWORDS = ["科幻", "悬疑", "古装", "青春", "家庭", "星际"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果-奇优影院` : "搜索影视内容-奇优影院",
    keyword
      ? `奇优影院搜索“${keyword}”的影视作品结果，包含相关电影与电视剧，可查看作品简介与分集目录。`
      : "在奇优影院搜索电影、电视剧与影视资讯，支持按片名、类型、演员关键词快速查找。",
    "影视搜索,电影搜索,电视剧搜索,奇优影院",
  );

  const results = useMemo(() => {
    if (!keyword) return [];
    const lower = keyword.toLowerCase();
    return titles.filter((item) => {
      const haystack = [item.title, item.region, item.director, ...item.genres, ...item.cast]
        .join(" ")
        .toLowerCase();
      return haystack.includes(lower);
    });
  }, [keyword]);

  const hotTitles = useMemo(() => [...titles].sort((a, b) => b.hot - a.hot).slice(0, 4), []);

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: trimmed });
  };

  return (
    <SiteLayout>
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-9">
        <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-6 md:px-7 md:py-8">
          <p className="text-[11px] tracking-[0.22em] text-accent-600 font-label uppercase mb-2">Search</p>
          <h1 className="text-xl md:text-3xl font-extrabold text-foreground-950 font-heading">
            搜索影视内容
          </h1>
          <p className="text-sm text-foreground-600 mt-1.5">
            支持按片名、类型、导演或演员关键词检索，电影与电视剧会同时出现在结果中。
          </p>

          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              submit(input);
            }}
            className="mt-4 flex flex-col sm:flex-row gap-2"
          >
            <div className="flex items-center gap-2 flex-1 h-11 px-3.5 rounded-md bg-background-50 border border-background-200 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-400 text-base w-5 h-5 flex items-center justify-center" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                type="search"
                name="search-keyword"
                placeholder="输入片名、类型或演员，如：科幻 / 长夜将明"
                aria-label="搜索影视内容"
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => {
                    setInput("");
                    setSearchParams({});
                  }}
                  aria-label="清除关键词"
                  className="w-6 h-6 rounded-full text-foreground-400 hover:text-foreground-700 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line text-sm w-4 h-4 flex items-center justify-center" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-700 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            <span className="text-xs text-foreground-500">热门关键词：</span>
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => {
                  setInput(word);
                  submit(word);
                }}
                className="px-2.5 py-1 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-600 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>
        </div>

        <section className="mt-6">
          {keyword ? (
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <p className="text-sm text-foreground-700">
                关键词 <span className="font-semibold text-accent-700">“{keyword}”</span> 共找到{" "}
                <span className="font-semibold text-foreground-950">{results.length}</span> 个结果
              </p>
              <button
                type="button"
                onClick={() => {
                  setInput("");
                  setSearchParams({});
                }}
                className="text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                清除关键词
              </button>
            </div>
          ) : (
            <p className="text-sm text-foreground-600 mb-4">输入关键词开始搜索，或先看看下面的热门推荐。</p>
          )}

          {keyword && results.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-5 py-12 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-300" />
              <p className="text-base font-medium text-foreground-900 mt-3">
                没有找到与“{keyword}”匹配的影视作品
              </p>
              <p className="text-sm text-foreground-500 mt-1">
                试试更换关键词，或从下面的热门推荐里挑一部看看。
              </p>
              <div className="flex flex-wrap items-center justify-center gap-1.5 mt-4">
                {["电影", "电视剧", "悬疑", "爱情"].map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => {
                      setInput(word);
                      submit(word);
                    }}
                    className="px-3 h-8 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            results.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {results.map((item) => (
                  <TitleCard key={item.id} item={item} />
                ))}
              </div>
            )
          )}
        </section>

        <section className="mt-9">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-foreground-950 font-heading">热门推荐</h2>
            <Link
              to="/category/movie"
              className="text-xs text-primary-700 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览全部分类
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {hotTitles.map((item) => (
              <TitleCard key={`hot-${item.id}`} item={item} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}